import React, { useState, useEffect, useContext } from 'react';
import Box from 'Components/Atoms/Box';
import Typography from 'Components/Atoms/Typography';
import Button from 'Components/Atoms/Button';
import IconButton from 'Components/Atoms/IconButton';
import TextField from 'Components/Atoms/TextField';
import { Close } from '@material-ui/icons';
import {
  RegularTable,
  RegularTableRepeating,
  EditReservation,
  TimeSlot,
  Reservation,
} from 'types/reservations';
import { ReservationGuest } from 'types/shared/helper/reservations';
import { db } from 'utils/firebase';
import { actingAs } from 'utils/reservationAuthor';
import DateDropdown from 'Components/Organisms/DateDropdown';
import TimeDropdown from 'App/ReservationBook/Components/TimeDropdown';
import TableDropdown from 'App/ReservationBook/Components/TableDropdown';
import useAvailableTimes from 'CustomHooks/useGetAvailableTimes';
import GuestsDropdown from 'App/ReservationBook/Components/GuestsDropdown';
import ReservationLength from 'App/ReservationBook/Components/ReservationLength';
import { ReservationContext } from 'Contexts/ReservationContext';
import { AuthContext } from 'Contexts/AuthContext';
import useToast from 'CustomHooks/useToast';
import usePinAuth from 'CustomHooks/usePinAuth';
import useRestaurant from 'CustomHooks/useRestaurant';
import { Severity } from 'Contexts/RestaurantContext';
import SignatureDropdown from 'Components/Organisms/SignatureDropdown';
import { SignatureContext } from 'Contexts/SignatureContext';
import { blockToTime, createRandomId, timeToBlock } from 'utils/helper';
import './AddEditRegularTableModal.scss';

export type AddEditRegularTableModalProps = {
  regularTable: RegularTable | null;
  restaurantId: string;
  onClose: () => void;
  onSave: () => void;
  fromReservation?: Reservation | null; // Optional: convert existing reservation to Regular Table
};

const AddEditRegularTableModal: React.FC<AddEditRegularTableModalProps> = ({
  regularTable,
  restaurantId,
  onClose,
  onSave,
  fromReservation,
}) => {
  const isEditing = !!regularTable;
  const isConverting = !!fromReservation; // Converting a reservation to Regular Table
  const {
    occassions = [],
    settings,
    reservationSettings,
  } = useContext(ReservationContext);
  const { uid } = useContext(AuthContext);
  const { validateSignature } = useContext(SignatureContext);
  const { experimentalFlags } = useRestaurant();
  const toast = useToast();
  const { authenticate, checkAuthentication } = usePinAuth();

  const sendEmailDefault =
    !experimentalFlags?.data?.confirmationEmailOffByDefault;
  const [loading, setLoading] = useState(false);
  // null lets the SignatureDropdown prefill the signature picked in the user menu
  const [waiterSignature, setWaiterSignature] = useState<string | null>(null);

  // Check if signature is required
  const signatureRequired =
    reservationSettings?.signatureRequired && !isEditing && !isConverting;

  // Form state - pre-fill from reservation if converting
  const [active, setActive] = useState(regularTable?.active ?? true);
  const [guestName, setGuestName] = useState(
    regularTable?.guest?.name ||
      (regularTable as any)?.reservation?.guest?.name ||
      fromReservation?.guest?.name ||
      ''
  );
  const [email, setEmail] = useState(
    regularTable?.guest?.email ||
      (regularTable as any)?.reservation?.guest?.email ||
      fromReservation?.guest?.email ||
      ''
  );
  const [phone, setPhone] = useState(
    regularTable?.guest?.phone ||
      (regularTable as any)?.reservation?.guest?.phone ||
      fromReservation?.guest?.phone ||
      ''
  );
  const [guests, setGuests] = useState(
    regularTable?.guests ||
      (regularTable as any)?.reservation?.guests ||
      fromReservation?.guests ||
      2
  );
  const [date, setDate] = useState(
    regularTable?.firstReservationAt ||
      (regularTable as any)?.reservation?.date ||
      fromReservation?.date ||
      new Date().toISOString().split('T')[0]
  );
  const [timeSlot, setTimeSlot] = useState<TimeSlot | null>(
    (fromReservation as any)?.timeSlot || null
  );
  // Store the original time string - support both new (top-level) and old (nested) schema
  const [reservationTime, setReservationTime] = useState(
    fromReservation?.time ||
      regularTable?.time ||
      (regularTable as any)?.reservation?.time ||
      ''
  );
  const [tables, setTables] = useState<string[]>(
    regularTable?.tables ||
      (regularTable as any)?.reservation?.tables ||
      fromReservation?.tables ||
      []
  );
  const [reservationLength, setReservationLength] = useState(
    regularTable?.reservationLength ||
      ((regularTable as any)?.reservation?.reservationLength
        ? (regularTable as any).reservation.reservationLength * 15
        : null) ||
      (fromReservation?.reservationLength
        ? fromReservation.reservationLength * 15
        : null) ||
      120
  );
  // The length dropdown and the times API work in 15-minute blocks
  const lengthInBlocks = Math.ceil(reservationLength / 15);
  const startBlock = timeToBlock(timeSlot?.time || reservationTime);
  const [space, setSpace] = useState(
    regularTable?.space ||
      (regularTable as any)?.reservation?.space ||
      fromReservation?.space ||
      ''
  );
  const [occassion, setOccassion] = useState(
    regularTable?.occassion ||
      (regularTable as any)?.reservation?.occassion ||
      fromReservation?.occassion ||
      occassions[0]?.id ||
      ''
  );
  const [sendEmail, setSendEmail] = useState(
    regularTable?.sendEmail ??
      (regularTable as any)?.reservation?.sendEmail ??
      (fromReservation
        ? ((fromReservation as any).sendEmail ?? sendEmailDefault)
        : sendEmailDefault)
  );
  const [firstReservationAt, setFirstReservationAt] = useState(
    regularTable?.firstReservationAt ||
      (regularTable as any)?.reservation?.date ||
      fromReservation?.date ||
      new Date().toISOString().split('T')[0]
  );
  const [lastReservation, setLastReservation] = useState(
    regularTable?.lastReservation || (regularTable as any)?.endDate || ''
  );
  const [xDaysInFuture, setXDaysInFuture] = useState(
    regularTable?.xDaysInFuture || 180
  );
  const [notes, setNotes] = useState(regularTable?.notes || '');

  // Availability checking - construct a minimal EditReservation for the hook
  const tempReservation: EditReservation = {
    date,
    occassion: occassion || occassions[0]?.id || '',
    guests: guests || 2,
    reservationLength: lengthInBlocks,
    timeSlot: timeSlot || null,
    tables: tables.length > 0 ? tables : null,
    guest: null,
  } as EditReservation;

  const { availableTimes } = useAvailableTimes(
    restaurantId,
    tempReservation,
    () => {},
    uid || ''
  );

  // A changed length refetches the times: take over the slot's new table combinations
  useEffect(() => {
    if (!timeSlot || !availableTimes) return;
    const fresh = availableTimes.find((t) => t.block === timeSlot.block);
    if (!fresh || fresh === timeSlot) return;
    setTimeSlot(fresh);
    if (!tables.length) return;
    const match = fresh.combinations.find(
      (c) =>
        c.tables.length === tables.length &&
        tables.every((t) => c.tables.includes(t))
    );
    // TableDropdown marks the selected combination by array identity
    setTables(match ? match.tables : []);
  }, [availableTimes, timeSlot, tables]);

  // Repeating pattern state - support both new `repeating` and old `rule` formats
  const ruleOrRepeating = regularTable?.repeating || (regularTable as any)?.rule;
  const ruleType = ruleOrRepeating?.type;

  // Map old rule type names to new repeating type names for the form
  const mapRuleTypeToRepeatType = (
    type: string | undefined
  ): RegularTableRepeating['type'] => {
    switch (type) {
      case 'weekly':
        return 'weekly';
      case 'monthlySpecificDay':
      case 'monthly_fix_day':
        return 'monthly_fix_day';
      case 'monthlyDayOfWeek':
      case 'monthly':
        return 'monthly';
      case 'yearlySpecificDate':
      case 'yearly':
        return 'yearly';
      default:
        return 'weekly';
    }
  };

  const [repeatType, setRepeatType] = useState<RegularTableRepeating['type']>(
    mapRuleTypeToRepeatType(ruleType)
  );
  const [weekDays, setWeekDays] = useState<number[]>(() => {
    if (ruleType === 'weekly') {
      return ruleOrRepeating?.weekDays || ruleOrRepeating?.days || [1];
    }
    return [1];
  });
  const [monthDay, setMonthDay] = useState(() => {
    if (ruleType === 'monthly_fix_day' || ruleType === 'monthlySpecificDay') {
      return ruleOrRepeating?.day || 1;
    }
    return 1;
  });
  const [yearlyDate, setYearlyDate] = useState(() => {
    // rule.date (dd.mm) is what the Cloud Function reads; other writers stored MM-DD or an ISO date
    const stored: string =
      (regularTable as any)?.rule?.date || ruleOrRepeating?.date || '';
    if (stored.includes('.')) return stored;
    const [day, month] = stored.split('-').reverse();
    return month ? `${day}.${month}` : '';
  });
  const [monthlyWeek, setMonthlyWeek] = useState(() => {
    if (ruleType === 'monthly' || ruleType === 'monthlyDayOfWeek') {
      return ruleOrRepeating?.week ?? ruleOrRepeating?.option ?? 1;
    }
    return 1;
  });
  const [monthlyWeekDay, setMonthlyWeekDay] = useState(() => {
    if (ruleType === 'monthly' || ruleType === 'monthlyDayOfWeek') {
      return ruleOrRepeating?.weekDay ?? ruleOrRepeating?.days?.[0] ?? 1;
    }
    return 1;
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!guestName.trim()) newErrors.guestName = 'Name ist erforderlich';
    if (!email.trim()) newErrors.email = 'E-Mail ist erforderlich';
    if (guests < 1) newErrors.guests = 'Mindestens 1 Gast erforderlich';
    // Time validation: either from timeSlot OR from reservation time (when converting)
    if (!timeSlot && !reservationTime)
      newErrors.time = 'Bitte wählen Sie eine Zeit';
    if (reservationLength < 15)
      newErrors.reservationLength = 'Mindestens 15 Minuten';
    if (!tables || tables.length === 0)
      newErrors.tables = 'Mindestens ein Tisch erforderlich';
    if (!firstReservationAt)
      newErrors.firstReservationAt = 'Startdatum ist erforderlich';

    // Validate repeating pattern
    if (repeatType === 'weekly' && weekDays.length === 0) {
      newErrors.weekDays = 'Mindestens ein Wochentag erforderlich';
    }
    if (repeatType === 'yearly') {
      // Same parsing as the Cloud Function that creates the reservations
      const [day, month] = yearlyDate.split('.').map(Number);
      // 2024 is a leap year, so 29.02 stays valid
      const parsed = new Date(2024, month - 1, day);
      if (
        !/^\d{1,2}\.\d{1,2}$/.test(yearlyDate) ||
        parsed.getMonth() !== month - 1 ||
        parsed.getDate() !== day
      ) {
        newErrors.yearlyDate = 'Datum im Format TT.MM, z. B. 24.12';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (pinChecked?: unknown) => {
    if (!validateForm() || loading) return;

    // Saving a regular table rewrites or deletes its linked reservations, so an
    // edit asks for the cancel right wherever a restaurant has set one.
    if ((isEditing || isConverting) && pinChecked !== true) {
      authenticate(
        isEditing && checkAuthentication('reservations.cancelReservation.write')
          ? 'reservations.cancelReservation.write'
          : 'reservations.editReservation.write',
        () => handleSave(true)
      );
      return;
    }

    setLoading(true);

    try {
      // Build repeating pattern
      let repeating: RegularTableRepeating;
      switch (repeatType) {
        case 'weekly':
          repeating = { type: 'weekly', weekDays };
          break;
        case 'monthly_fix_day':
          repeating = { type: 'monthly_fix_day', day: monthDay };
          break;
        case 'yearly':
          repeating = { type: 'yearly', date: yearlyDate };
          break;
        case 'monthly':
          repeating = {
            type: 'monthly',
            week: monthlyWeek,
            weekDay: monthlyWeekDay,
          };
          break;
      }

      const guest: ReservationGuest = {
        name: guestName,
        email,
        phone,
      } as ReservationGuest;

      // Convert our repeating pattern to old backend rule format for compatibility
      let rule: any;
      switch (repeatType) {
        case 'weekly':
          rule = { type: 'weekly', days: weekDays, every: 1, offset: 0 };
          break;
        case 'monthly_fix_day':
          rule = { type: 'monthlySpecificDay', day: monthDay };
          break;
        case 'yearly':
          rule = { type: 'yearlySpecificDate', date: yearlyDate };
          break;
        case 'monthly':
          rule = {
            type: 'monthlyDayOfWeek',
            option: monthlyWeek,
            days: [monthlyWeekDay],
          };
          break;
      }

      // Use timeSlot.time if available, otherwise fall back to reservationTime
      const finalTime = timeSlot?.time || reservationTime || '19:00';

      // For the regularTable document, store reservationLength in MINUTES
      const tableData: any = {
        active,
        restaurant: restaurantId,
        guest,
        guests,
        firstReservationAt,
        sendEmail,
        time: finalTime,
        reservationLength, // Store in minutes for the new system
        tables: tables,
        xDaysInFuture,
        repeating,
        rule, // Backend compatibility (old field)
        updatedAt: Date.now(),
        // Create nested reservation object for OLD backend compatibility
        // Old system expects reservationLength in BLOCKS (15-min intervals)
        reservation: {
          guest,
          date: firstReservationAt,
          time: finalTime,
          reservationLength: Math.ceil(reservationLength / 15), // Store in blocks for old system
          tables,
          space: space || null,
          occassion: occassion || null,
          guests,
          sendEmail,
        },
      };

      // Only add optional fields if they have values (Firebase doesn't accept undefined)
      if (lastReservation) {
        tableData.lastReservation = lastReservation;
        tableData.endDate = lastReservation; // Backend compatibility
      }
      if (space) tableData.space = space;
      if (occassion) tableData.occassion = occassion;
      if (notes) tableData.notes = notes;

      if (!isEditing && !isConverting) {
        tableData.createdAt = Date.now();
      }

      if (isEditing && regularTable?.id) {
        // Update existing regular table
        await db
          .collection('regularTables')
          .doc(regularTable.id)
          .update(tableData);
      } else if (isConverting && fromReservation?.id) {
        // Converting from reservation - use reservation ID as document ID
        const regularTableData = {
          ...tableData,
          id: fromReservation.id, // ✅ ID field (same as reservation ID)
        };

        // Create the regular table document
        await db
          .collection('regularTables')
          .doc(fromReservation.id)
          .set(regularTableData);

        // Update the existing reservation to link it to the regular table
        await db.collection('requestsV03').doc(fromReservation.id).update({
          stammtischId: fromReservation.id, // Old field name
          regularTableId: fromReservation.id, // New field name
          updatedAt: Date.now(),
          updatedBy: waiterSignature || actingAs(restaurantId, uid) || '',
        });
      } else {
        // Create new regular table from scratch:
        // STEP 1: Generate Firestore-style long ID (like old system)
        const reservationId = db.collection('requestsV03').doc().id; // ✅ Firestore auto-generated ID (20 chars)
        const customId = createRandomId();
        const now = Date.now();

        // Calculate time blocks (reservations use 15-minute blocks)
        const startTimeInBlocks = timeToBlock(finalTime);
        const reservationLengthInBlocks = Math.ceil(reservationLength / 15);
        const endTimeInBlocks = startTimeInBlocks + reservationLengthInBlocks;

        // Create table string (e.g., "T1, T2")
        const tableStr = tables.join(', ');

        // Add guestId to guest object (required for customer lookup)
        const guestWithId = {
          ...guest,
          id: email, // Use email as guest identifier
          guestId: email,
        };

        const newReservation = {
          id: reservationId,
          restaurant: restaurantId,
          guest: guestWithId,
          guests,
          date: firstReservationAt,
          time: finalTime,
          startTimeInBlocks,
          endTimeInBlocks,
          reservationLength: reservationLengthInBlocks, // Stored in blocks
          tables,
          tableStr,
          space: space || null,
          occassion: occassion || null,
          sendEmail,
          customId, // Required for tracking
          source: 'inHouse', // Required field
          status: 'success', // Use 'success' not 'confirmed'
          validTill: 0, // 0 means reservation is finalized
          createdAt: now,
          updatedAt: now,
          createdBy: waiterSignature || actingAs(restaurantId, uid) || '',
          updatedBy: waiterSignature || actingAs(restaurantId, uid) || '',
          stammtischId: reservationId, // ✅ Old field name for backward compatibility
          regularTableId: reservationId, // ✅ New field name - link first reservation to regular table
        };

        // Create the reservation
        console.log('📝 Creating reservation with ID:', reservationId);
        console.log('📋 Reservation data:', newReservation);
        await db
          .collection('requestsV03')
          .doc(reservationId)
          .set(newReservation);

        // STEP 2: Create the regular table linked to the reservation
        // Build complete regular table data with ID included
        const regularTableData = {
          ...tableData,
          id: reservationId, // ✅ ID field (same as reservation ID)
          createdAt: now,
        };

        console.log('🔄 Creating regular table with ID:', reservationId);
        console.log('📋 Regular table data:', regularTableData);
        console.log('✅ Regular table ID field value:', regularTableData.id);

        await db
          .collection('regularTables')
          .doc(reservationId)
          .set(regularTableData);

        console.log('✅ Successfully created reservation and regular table');
      }

      toast(
        isEditing
          ? 'Stammtisch erfolgreich aktualisiert!'
          : isConverting
          ? 'Reservierung in Stammtisch umgewandelt!'
          : 'Stammtisch erfolgreich erstellt!',
        Severity.SUCCESS
      );

      onClose();
      onSave();
    } catch (error: any) {
      console.error('❌ Error saving regular table:', error);
      toast(
        error?.message || 'Fehler beim Speichern des Stammtisches',
        Severity.ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleWeekDay = (day: number) => {
    setWeekDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()
    );
  };

  return (
    <>
      <Box className="add-edit-modal-overlay" onClick={onClose} />
      <Box className="add-edit-regular-table-modal">
        <Box className="modal-header">
          <Typography variant="h3">
            {isEditing
              ? 'Stammtisch bearbeiten'
              : isConverting
              ? 'Als Stammtisch einrichten'
              : 'Neuer Stammtisch'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        <Box className="modal-content">
          {/* Info banner when converting */}
          {isConverting && (
            <Box
              style={{
                padding: '1rem',
                marginBottom: '1.5rem',
                backgroundColor: '#e8f4fd',
                border: '1px solid #2196f3',
                borderRadius: '4px',
              }}
            >
              <Typography variant="text-3" style={{ color: '#1565c0' }}>
                ℹ️ Diese Reservierung wird als Stammtisch eingerichtet. Die
                Details der Reservierung sind vorausgefüllt. Konfigurieren Sie
                unten das Wiederholungsmuster.
              </Typography>
            </Box>
          )}

          {/* Status */}
          <Box className="form-section">
            <Typography
              variant="text-5"
              weight="bold"
              className="section-label"
            >
              STATUS
            </Typography>
            <Box className="form-field">
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>Stammtisch ist aktiv</span>
              </label>
            </Box>
          </Box>

          {/* Guest Information */}
          <Box className="form-section">
            <Typography
              variant="text-5"
              weight="bold"
              className="section-label"
            >
              GAST INFORMATIONEN
            </Typography>
            <Box className="form-field">
              <label>Name *</label>
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                className={errors.guestName ? 'error' : ''}
              />
              {errors.guestName && (
                <span className="error-text">{errors.guestName}</span>
              )}
            </Box>
            <Box className="form-field">
              <label>E-Mail *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={errors.email ? 'error' : ''}
              />
              {errors.email && (
                <span className="error-text">{errors.email}</span>
              )}
            </Box>
            <Box className="form-field" style={{ marginBottom: '1rem' }}>
              <TextField
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                type="phone"
                label="Telefon"
                labelTranslation="reservations"
                defaultCountry="de"
              />
            </Box>
          </Box>

          {/* Reservation Details */}
          <Box className="form-section">
            <Typography
              variant="text-5"
              weight="bold"
              className="section-label"
            >
              RESERVIERUNG DETAILS
            </Typography>

            <Box className="form-field">
              <label>Anlass *</label>
              <select
                value={occassion}
                onChange={(e) => {
                  setOccassion(e.target.value);
                  setTimeSlot(null);
                  setTables([]);
                }}
              >
                {occassions.length > 0 ? (
                  occassions.map((occ) => (
                    <option key={occ.id} value={occ.id}>
                      {occ.title}
                    </option>
                  ))
                ) : (
                  <option value="">Kein Anlass verfügbar</option>
                )}
              </select>
            </Box>

            <Box className="form-field">
              <label>Datum für Verfügbarkeitsprüfung *</label>
              <DateDropdown
                value={date}
                onChange={(newDate) => {
                  setDate(newDate);
                  setTimeSlot(null);
                  setTables([]);
                }}
              />
            </Box>

            <Box className="form-field">
              <label>Anzahl Gäste *</label>
              <GuestsDropdown
                value={guests}
                onChange={(newGuests) => {
                  setGuests(newGuests);
                  setTimeSlot(null);
                  setTables([]);
                }}
              />
            </Box>

            <Box className="form-field">
              <label>Zeit *</label>
              <TimeDropdown
                value={timeSlot}
                timeSlots={availableTimes}
                onChange={(newSlot) => {
                  setTimeSlot(newSlot);
                  setTables([]);
                }}
              />
              {errors.time && <span className="error-text">{errors.time}</span>}
              <Typography
                variant="text-4"
                color="subdued"
                style={{ marginTop: '0.25rem' }}
              >
                {reservationTime && !timeSlot
                  ? `Zeit aus Reservierung: ${reservationTime} (Sie können eine neue Zeit wählen)`
                  : `Zeiten basierend auf Verfügbarkeit für ${date} (${
                      occassions.find((o) => o.id === occassion)?.title ||
                      occassion
                    })`}
              </Typography>
            </Box>

            <Box className="form-field">
              <label>Dauer (Stunden) *</label>
              <ReservationLength
                value={lengthInBlocks}
                onChange={(newLength) => setReservationLength(newLength * 15)}
              />
              {!isNaN(startBlock) && (
                <Typography
                  variant="text-4"
                  color="subdued"
                  style={{ marginTop: '0.25rem' }}
                >
                  Reserviert bis {blockToTime(startBlock + lengthInBlocks)} Uhr
                </Typography>
              )}
            </Box>

            <Box className="form-field">
              <label>Tische *</label>
              {timeSlot ? (
                <TableDropdown
                  value={tables}
                  combinations={timeSlot?.combinations || null}
                  onChange={(newTables) => setTables(newTables)}
                />
              ) : (
                <input
                  type="text"
                  value={tables.join(', ')}
                  onChange={(e) =>
                    setTables(
                      e.target.value
                        .split(',')
                        .map((t) => t.trim())
                        .filter((t) => t)
                    )
                  }
                  placeholder="z.B. T1, T2, T3"
                  className={errors.tables ? 'error' : ''}
                />
              )}
              {errors.tables && (
                <span className="error-text">{errors.tables}</span>
              )}
              {!timeSlot && tables.length > 0 && (
                <Typography
                  variant="text-4"
                  style={{ color: '#666', marginTop: '0.25rem' }}
                >
                  Tische aus Reservierung: {tables.join(', ')} (Sie können neue
                  Tische wählen, indem Sie eine Zeit auswählen)
                </Typography>
              )}
            </Box>
            <Box className="form-field">
              <label>Raum</label>
              <input
                type="text"
                value={space}
                onChange={(e) => setSpace(e.target.value)}
              />
            </Box>
            <Box className="form-field">
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                />
                <span>Bestätigungs-E-Mail senden</span>
              </label>
            </Box>
          </Box>

          {/* Repeating Pattern */}
          <Box className="form-section">
            <Typography
              variant="text-5"
              weight="bold"
              className="section-label"
            >
              WIEDERHOLUNG
            </Typography>
            <Box className="form-field">
              <label>Wiederholungstyp *</label>
              <select
                value={repeatType}
                onChange={(e) => setRepeatType(e.target.value as any)}
              >
                <option value="weekly">Wöchentlich</option>
                <option value="monthly_fix_day">Monatlich (fester Tag)</option>
                <option value="monthly">
                  Monatlich (z.B. erster Sonntag)
                </option>
                <option value="yearly">Jährlich</option>
              </select>
            </Box>

            {repeatType === 'weekly' && (
              <Box className="form-field">
                <label>Wochentage *</label>
                <Box className="weekday-selector">
                  {['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'].map(
                    (day, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`weekday-btn ${
                          weekDays.includes(index) ? 'selected' : ''
                        }`}
                        onClick={() => toggleWeekDay(index)}
                      >
                        {day}
                      </button>
                    )
                  )}
                </Box>
                {errors.weekDays && (
                  <span className="error-text">{errors.weekDays}</span>
                )}
              </Box>
            )}

            {repeatType === 'monthly_fix_day' && (
              <Box className="form-field">
                <label>Tag des Monats *</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={monthDay}
                  onChange={(e) => setMonthDay(parseInt(e.target.value) || 1)}
                />
              </Box>
            )}

            {repeatType === 'yearly' && (
              <Box className="form-field">
                <label>Datum (TT.MM) *</label>
                <input
                  type="text"
                  value={yearlyDate}
                  onChange={(e) => setYearlyDate(e.target.value.trim())}
                  placeholder="24.12"
                  className={errors.yearlyDate ? 'error' : ''}
                />
                {errors.yearlyDate && (
                  <span className="error-text">{errors.yearlyDate}</span>
                )}
              </Box>
            )}

            {repeatType === 'monthly' && (
              <Box className="form-grid">
                <Box className="form-field">
                  <label>Woche *</label>
                  <select
                    value={monthlyWeek}
                    onChange={(e) => setMonthlyWeek(parseInt(e.target.value))}
                  >
                    <option value="1">Erste</option>
                    <option value="2">Zweite</option>
                    <option value="3">Dritte</option>
                    <option value="4">Vierte</option>
                  </select>
                </Box>
                <Box className="form-field">
                  <label>Wochentag *</label>
                  <select
                    value={monthlyWeekDay}
                    onChange={(e) =>
                      setMonthlyWeekDay(parseInt(e.target.value))
                    }
                  >
                    <option value="0">Sonntag</option>
                    <option value="1">Montag</option>
                    <option value="2">Dienstag</option>
                    <option value="3">Mittwoch</option>
                    <option value="4">Donnerstag</option>
                    <option value="5">Freitag</option>
                    <option value="6">Samstag</option>
                  </select>
                </Box>
              </Box>
            )}
          </Box>

          {/* Date Range */}
          <Box className="form-section">
            <Typography
              variant="text-5"
              weight="bold"
              className="section-label"
            >
              ZEITRAUM
            </Typography>
            <Box className="form-grid">
              <Box className="form-field">
                <label>Startdatum *</label>
                <input
                  type="date"
                  value={firstReservationAt}
                  onChange={(e) => setFirstReservationAt(e.target.value)}
                  className={errors.firstReservationAt ? 'error' : ''}
                />
                {errors.firstReservationAt && (
                  <span className="error-text">
                    {errors.firstReservationAt}
                  </span>
                )}
              </Box>
              <Box className="form-field">
                <label>Enddatum (optional)</label>
                <input
                  type="date"
                  value={lastReservation}
                  onChange={(e) => setLastReservation(e.target.value)}
                />
                {lastReservation && (
                  <Typography
                    variant="text-4"
                    color="subdued"
                    style={{ marginTop: '0.25rem' }}
                  >
                    Reservierungen werden bis{' '}
                    {new Date(lastReservation).toLocaleDateString('de-DE')}{' '}
                    erstellt
                  </Typography>
                )}
              </Box>
            </Box>
            {!lastReservation && (
              <Box className="form-field">
                <label>Tage im Voraus</label>
                <input
                  type="number"
                  min="30"
                  max="365"
                  value={xDaysInFuture}
                  onChange={(e) =>
                    setXDaysInFuture(parseInt(e.target.value) || 180)
                  }
                />
                <Typography
                  variant="text-4"
                  style={{ color: '#666', marginTop: '0.25rem' }}
                >
                  Reservierungen werden automatisch für die nächsten{' '}
                  {xDaysInFuture} Tage erstellt (Standard: 180)
                </Typography>
              </Box>
            )}
          </Box>

          {/* Notes */}
          <Box className="form-section">
            <Typography
              variant="text-5"
              weight="bold"
              className="section-label"
            >
              NOTIZEN
            </Typography>
            <Box className="form-field">
              <label>Interne Notizen</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Zusätzliche Informationen zu diesem Stammtisch..."
              />
            </Box>
          </Box>

          {/* Signature - only show when creating new (not editing, not converting) */}
          {signatureRequired && (
            <Box className="form-section">
              <Typography
                variant="text-5"
                weight="bold"
                className="section-label"
              >
                UNTERSCHRIFT *
              </Typography>
              <Box className="form-field">
                <SignatureDropdown
                  value={waiterSignature}
                  onChange={(newSignature) => setWaiterSignature(newSignature)}
                />
                {errors.signature && (
                  <span className="error-text">{errors.signature}</span>
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Box className="modal-footer">
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading
              ? 'Speichert...'
              : isEditing
              ? 'Änderungen speichern'
              : isConverting
              ? 'Als Stammtisch speichern'
              : 'Stammtisch erstellen'}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default AddEditRegularTableModal;
